import React, { useState, useEffect } from 'react';
import { 
  ArrowDownTrayIcon, 
  LockClosedIcon, 
  SparklesIcon,
  ChartBarIcon,
  TrendingUpIcon,
  CurrencyDollarIcon,
  ClockIcon,
  StarIcon
} from '@heroicons/react/24/outline';
import { 
  CheckCircleIcon 
} from '@heroicons/react/24/solid';
import BottomNav from '../components/mobile/BottomNav';
import { useTranslations } from '../hooks/useTranslations';

import channelPatternDetector from '../assets/indicators/ChannelPatternDetector.zip';
import easyTrendVisualizer from '../assets/indicators/EasyTrendVisualizer.zip';
import marketProfile from '../assets/indicators/MarketProfile.zip';
import mt4RsiWithAlert from '../assets/indicators/MQLTA MT4 RSI With Alert.zip';
import mt4VolumeProfile from '../assets/indicators/MQLTA MT4 Volume Profile.zip';

function Indicators() {
  const { t } = useTranslations();
  const [userData, setUserData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [downloadingId, setDownloadingId] = useState(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  const indicators = [
    {
      id: 1,
      name: t('indicators.items.0.name'),
      description: t('indicators.items.0.description'),
      size: '7 КБ',
      fileName: 'ChannelPatternDetector.zip',
      fileUrl: channelPatternDetector,
      access: 'all',
    },
    {
      id: 2,
      name: t('indicators.items.1.name'),
      description: t('indicators.items.1.description'),
      size: '31 КБ',
      fileName: 'EasyTrendVisualizer.zip',
      fileUrl: easyTrendVisualizer,
      access: 'all',
    },
    {
      id: 3,
      name: t('indicators.items.2.name'),
      description: t('indicators.items.2.description'),
      size: '242 КБ',
      fileName: 'MarketProfile.zip',
      fileUrl: marketProfile,
      access: 'all',
    },
    {
      id: 4,
      name: t('indicators.items.3.name'),
      description: t('indicators.items.3.description'),
      size: '29 КБ',
      fileName: 'MQLTA MT4 RSI With Alert.zip',
      fileUrl: mt4RsiWithAlert,
      access: 'all',
    },
    {
      id: 5,
      name: t('indicators.items.4.name'),
      description: t('indicators.items.4.description'),
      size: '30 КБ',
      fileName: 'MQLTA MT4 Volume Profile.zip',
      fileUrl: mt4VolumeProfile,
      access: 'all',
    },
  ];

  useEffect(() => {
    const fetchUserData = async () => {
      const token = localStorage.getItem('token');
      if (!token) {
        setIsLoading(false);
        return;
      }

      try {
        const response = await fetch("https://api.maxprofitacademy.com/user", {
          method: "GET",
          headers: {
            "token": JSON.parse(token)
          }
        });

        const data = await response.json();
        if (data.status === 200) {
          setUserData(data.data);
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchUserData();
  }, []);

  const getUserCourse = (userData) => (userData?.course || '').toUpperCase();
  const userCourse = getUserCourse(userData);
  const isGoldOrPlatinum = userCourse === 'GOLD' || userCourse === 'PLATINUM';
  const isNoAccess = !userCourse || userCourse === 'NONE' || userCourse === 'OVERDRAFT';

  // Если нет доступа — индикаторы не показываем
  const filteredIndicators = isNoAccess ? [] : indicators;

  // Защищённая функция скачивания
  const handleDownload = async (indicator) => {
    // Проверяем доступ
    if (indicator.access === 'gold' && !isGoldOrPlatinum) {
      setShowUpgradeModal(true);
      return;
    }
    setDownloadingId(indicator.id);
    try {
      // Только если есть доступ — скачиваем
      if (indicator.access === 'all' || isGoldOrPlatinum) {
        const link = document.createElement('a');
        link.href = indicator.fileUrl;
        link.download = indicator.fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        // Даже если кто-то вызовет функцию напрямую — ничего не произойдёт
        setShowUpgradeModal(true);
      }
    } catch (error) {
      alert(t('indicators.downloadError'));
    } finally {
      setDownloadingId(null);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
        <span className="ml-3 text-gray-600">{t('indicators.loading')}</span>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      {/* Indicators Grid */}
      {isNoAccess && (
        <div className="text-center py-12">
          <LockClosedIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500 text-lg font-semibold">{t('indicators.noAccess')}</p>
          <p className="text-gray-400">{t('indicators.noAccessSubtitle')}</p>
        </div>
      )}
      {!isNoAccess && (
        <div className="pb-20">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredIndicators.map((indicator, index) => (
              <div
                key={indicator.id}
                className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-lg transition-all duration-300 hover:-translate-y-1 animate-in slide-in-from-bottom-4 duration-300"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                {/* Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className="bg-primary/10 p-3 rounded-xl hover:bg-primary/20 transition-colors duration-200 hover:scale-110 transform">
                    <ChartBarIcon className="h-6 w-6 text-primary" />
                  </div>
                  {indicator.access === 'gold' && (
                    <div className="bg-gradient-to-r from-yellow-400 to-yellow-500 text-white px-3 py-1 rounded-full text-xs font-semibold flex items-center animate-pulse">
                      <SparklesIcon className="h-3 w-3 mr-1" />
                      {t('indicators.premium')}
                    </div>
                  )}
                </div>

                {/* Content */}
                <div className="mb-4">
                  <h3 className="text-lg font-bold text-gray-900 mb-2">{indicator.name}</h3>
                  <p className="text-gray-600 text-sm mb-3">{indicator.description}</p>
                </div>

                {/* Download Button */}
                <button
                  onClick={() => handleDownload(indicator)}
                  disabled={downloadingId === indicator.id || (indicator.access === 'gold' && !isGoldOrPlatinum)}
                  className={`w-full py-3 px-4 rounded-xl font-semibold transition-all duration-200 flex items-center justify-center space-x-2 transform hover:scale-105 active:scale-95 ${
                    (isGoldOrPlatinum || indicator.access === 'all')
                      ? 'bg-gradient-to-r from-primary to-primary/90 text-white hover:from-primary/90 hover:to-primary shadow-lg'
                      : 'bg-gray-100 text-gray-500 cursor-not-allowed opacity-60'
                  }`}
                >
                  {downloadingId === indicator.id ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                      <span className="animate-pulse">{t('indicators.downloading')}</span>
                    </>
                  ) : (isGoldOrPlatinum || indicator.access === 'all') ? (
                    <>
                      <ArrowDownTrayIcon className="h-5 w-5 animate-bounce hover:scale-110 transition-transform duration-200" />
                      <span>{t('indicators.download')}</span>
                    </>
                  ) : (
                    <>
                      <LockClosedIcon className="h-5 w-5 animate-pulse hover:scale-110 transition-transform duration-200" />
                      <span>{t('indicators.unavailable')}</span>
                    </>
                  )}
                </button>
              </div>
            ))}
          </div>

          {filteredIndicators.length === 0 && (
            <div className="text-center py-12">
              <ChartBarIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">{t('indicators.notFound')}</p>
            </div>
          )}
        </div>
      )}

      {/* Upgrade Modal */}
      {showUpgradeModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 animate-in fade-in duration-300">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full animate-in slide-in-from-bottom-4 duration-300">
            <div className="text-center mb-6">
              <div className="bg-gradient-to-r from-yellow-400 to-yellow-500 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 animate-pulse">
                <SparklesIcon className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">{t('indicators.upgradeModal.title')}</h3>
              <p className="text-gray-600">{t('indicators.upgradeModal.subtitle')}</p>
            </div>

            <div className="space-y-4 mb-6">
              <div className="flex items-center space-x-3 animate-in slide-in-from-left-2 duration-300 delay-100">
                <CheckCircleIcon className="h-5 w-5 text-green-500" />
                <span className="text-sm">{t('indicators.upgradeModal.features.professional')}</span>
              </div>
              <div className="flex items-center space-x-3 animate-in slide-in-from-left-2 duration-300 delay-200">
                <CheckCircleIcon className="h-5 w-5 text-green-500" />
                <span className="text-sm">{t('indicators.upgradeModal.features.signals')}</span>
              </div>
              <div className="flex items-center space-x-3 animate-in slide-in-from-left-2 duration-300 delay-300">
                <CheckCircleIcon className="h-5 w-5 text-green-500" />
                <span className="text-sm">{t('indicators.upgradeModal.features.updates')}</span>
              </div>
              <div className="flex items-center space-x-3 animate-in slide-in-from-left-2 duration-300 delay-400">
                <CheckCircleIcon className="h-5 w-5 text-green-500" />
                <span className="text-sm">{t('indicators.upgradeModal.features.strategies')}</span>
              </div>
            </div>

            <div className="flex space-x-3">
              <button
                onClick={() => setShowUpgradeModal(false)}
                className="flex-1 py-3 px-4 border border-gray-300 rounded-xl text-gray-700 font-medium hover:bg-gray-50 transition-colors"
              >
                {t('indicators.upgradeModal.later')}
              </button>
              <button
                onClick={() => {
                  setShowUpgradeModal(false);
                  // Здесь можно добавить навигацию к странице оплаты
                }}
                className="flex-1 py-3 px-4 bg-gradient-to-r from-primary to-primary/90 text-white rounded-xl font-medium hover:from-primary/90 hover:to-primary transition-all duration-200 transform hover:scale-105"
              >
                {t('indicators.upgradeModal.upgrade')}
              </button>
            </div>
          </div>
        </div>
      )}

      <BottomNav />
    </div>
  );
}

export default Indicators; 